import { Component, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Game error caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/95 z-50">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-3xl font-bold text-primary neon-text mb-4">
              {t('error.title')}
            </h1>
            <p className="text-muted-foreground mb-6">
              {t('error.message')}
            </p>
            <div className="space-y-4">
              <button
                onClick={this.handleRetry}
                className="neon-button w-full py-3"
              >
                {t('error.restart')}
              </button>
              <p className="text-xs text-muted-foreground">
                {t('error.fallback')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryClass);
